import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { ClassroomModalRouteAction } from 'components/ClassroomsModule/types';
import EditStudentModal from 'components/ClassroomsModule/ClassroomDetails/modals/EditStudentModal';
import ManageStudentProductsModal from 'components/ClassroomsModule/ClassroomDetails/modals/ManageStudentProductsModal';
import PrintCertificatesCardsModal from 'components/ClassroomsModule/ClassroomDetails/modals/PrintCertificatesCardsModal';
import PrintStudentLoginCardsModal from 'components/ClassroomsModule/ClassroomDetails/modals/PrintStudentLoginCardsModal';
import TransferStudentModal from 'components/ClassroomsModule/ClassroomDetails/modals/TransferStudentModal';
import StudentSettingsModal from 'components/ClassroomsModule/Classrooms/modals/StudentSettingsModal';
import Config from 'config';
import GET_CLASSROOM from 'graph/classroom/get-classroom-query';
import REMOVE_STUDENT from 'graph/student/remove-student-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import { ActionOption } from 'types';
import { AlertVariant } from 'types/enums';
import { getFullName } from 'utilities/name-utils';
import { Student, Classroom } from 'types/schema-generated-types';
import useRouteAction from './route-action/use-route-action';
import { ModificationAlertContent } from './sweet-alert/use-modification-alert';

export type UseStudentActionOptionsMenu = {
  actionOptions: ActionOption[];
  archivedOptions: ActionOption[];
  loading: boolean;
};
export type UseStudentActionOptionMenuProps = {
  translationId: string;
  classroomId: number;
  classroom: Classroom;
  refetchClassroom?: () => void;
};
const { privilegesGroups } = Config;

const getClassroomMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_CLASSROOM,
      variables: {
        id: classroomId
      },
      reducer: (cachedData, { removeStudent }): { classroom: any } => {
        return {
          classroom: {
            ...cachedData?.classroom,
            students: cachedData?.classroom?.students?.filter(
              (student: Student) => student.id !== removeStudent.id
            )
          }
        };
      }
    }
  ])
});

const useStudentActionOptionsMenuProps = ({
  translationId,
  classroomId,
  classroom,
  refetchClassroom
}: UseStudentActionOptionMenuProps): UseStudentActionOptionsMenu => {
  const { t } = useTranslation(translationId);
  const params = useParams<any>();
  const [
    { userPrivilegesCache },
    { openFullModal, openQuarterModal, openAlert, closeModal }
  ] = useStore();
  const [removeStudent, { loading: removeStudentMutationLoading }] = useMutation(
    REMOVE_STUDENT,
    getClassroomMutationCacheUpdate(Number(classroomId))
  );
  const archived = Boolean(classroom?.dateArchived);

  const handleEditStudentSettingsClick = (event: React.MouseEvent<HTMLElement>): void => {
    openEditStudentSettingsModal(Number(event.currentTarget.id));
  };

  const openEditStudentSettingsModal = (studentId: number): void => {
    openFullModal({
      title: null,
      body: <StudentSettingsModal />,
      routeAction: ClassroomModalRouteAction.EDIT_STUDENT_SETTINGS,
      routeParams: { studentId, classroomId }
    });
  };

  const handleEditStudentProductsClick = (event: React.MouseEvent<HTMLLIElement>): void => {
    openEditStudentProductsModal(event.currentTarget.id);
  };

  const handleEditStudentDetailsClick = (event: React.MouseEvent<HTMLLIElement>): void => {
    openEditStudentDetailsModal(event.currentTarget.id, params.classroomId);
  };

  const onEditStudentSubmitSuccess = (): void => {
    closeModal();
    openAlert({
      variant: AlertVariant.SUCCESS,
      title: t('Student Details Updated'),
      text: t('The student details have been successfully updated.')
    });
  };

  const handleTransferStudentClick = (event: React.MouseEvent<HTMLLIElement>): void => {
    openTransferStudentDetailsModal(event.currentTarget.id);
  };

  const onTransferStudentSubmitSuccess = (): void => {
    closeModal();
    openAlert({
      variant: AlertVariant.SUCCESS,
      title: t('Student Transfer Success'),
      text: t('The student has been successfully transferred.')
    });
  };

  const openTransferStudentDetailsModal = (studentId: string): void => {
    openQuarterModal({
      body: <TransferStudentModal onSubmitSuccess={onTransferStudentSubmitSuccess} />,
      routeAction: ClassroomModalRouteAction.TRANSFER_STUDENT,
      routeParams: { studentId, classroomId }
    });
  };

  const openEditStudentProductsModal = (studentId: string): void => {
    openFullModal({
      body: <ManageStudentProductsModal onClose={closeModal} />,
      options: { hideCloseButton: true },
      routeAction: ClassroomModalRouteAction.EDIT_STUDENT_PRODUCTS,
      routeParams: { studentId, classroomId }
    });
  };

  const openEditStudentDetailsModal = (studentId: string, classroomId: string): void => {
    openQuarterModal({
      body: <EditStudentModal onSubmitSuccess={onEditStudentSubmitSuccess} onClose={closeModal} />,
      routeAction: ClassroomModalRouteAction.EDIT_STUDENT,
      routeParams: { studentId, classroomId }
    });
  };

  const handleConfirmedDelete = (studentId: number) => async (): Promise<void> => {
    try {
      await removeStudent({ variables: { where: { id: studentId } } });
      await refetchClassroom?.();

      openAlert({
        variant: AlertVariant.SUCCESS,
        title: t('successTitle', { ns: 'DeleteStudentsAlert' }),
        text: t('successMessage', { ns: 'DeleteStudentsAlert' })
      });
    } catch (error) {
      //TODO: Handle error for delete
      console.log('Error', error);
    }
  };

  const getSelectedStudent = (studentId: number) =>
    classroom?.students?.find((x: any) => x.id === studentId);

  const handleDeleteStudentAlert = (event: React.MouseEvent<HTMLElement>): void => {
    const selectedStudent = getSelectedStudent(Number(event.currentTarget.id));
    if (selectedStudent) {
      const textItems = [getFullName(selectedStudent)];
      const deleteText = t('deleteConfirmInstructions', { ns: '@common' });
      const title = t('title', { count: textItems.length, ns: 'DeleteStudentsAlert' });
      const confirmButtonText = t('confirm', {
        count: textItems.length,
        ns: 'DeleteStudentsAlert'
      });

      openAlert({
        variant: AlertVariant.DELETE,
        textItems,
        title,
        confirmButtonText,
        deleteText,
        html: <ModificationAlertContent textItems={textItems} deleteText={deleteText} />,
        onSubmit: handleConfirmedDelete(selectedStudent.id)
      });
    }
  };

  const openPrintLoginCardsModal = (event: React.MouseEvent<HTMLLIElement>): void => {
    openQuarterModal({
      body: <PrintStudentLoginCardsModal />,
      routeAction: ClassroomModalRouteAction.PRINT_STUDENT_LOGIN_CARDS,
      routeParams: { studentId: Number(event.currentTarget.id), classroomId: Number(classroomId) }
    });
  };

  const openPrintCertificatesModal = (event: React.MouseEvent<HTMLLIElement>): void => {
    openQuarterModal({
      body: <PrintCertificatesCardsModal />,
      routeAction: ClassroomModalRouteAction.PRINT_STUDENT_CERTIFICATES,
      routeParams: { studentId: Number(event.currentTarget.id) }
    });
  };

  const archivedOptions = [
    {
      label: 'Delete Student',
      MenuItemProps: { onClick: handleDeleteStudentAlert },
      isPrivileged: (): boolean =>
        userPrivilegesCache.isPrivileged(privilegesGroups.mutateStudent, {
          classroomId,
          organizationId: classroom?.organizationId
        })
    }
  ];
  const actionOptions: ActionOption[] = archived
    ? archivedOptions
    : [
        {
          label: 'Manage Student Settings',
          MenuItemProps: { onClick: handleEditStudentSettingsClick }
        },
        {
          label: 'Manage Student Products',
          MenuItemProps: { onClick: handleEditStudentProductsClick },
          isPrivileged: (): boolean =>
            userPrivilegesCache.isPrivileged(
              [...privilegesGroups.updateClassroomLicense, ...privilegesGroups.updateOrgLicense],
              {
                classroomId,
                organizationId: classroom?.organizationId
              }
            )
        },
        {
          label: 'Print Student Login Cards',
          MenuItemProps: {
            onClick: openPrintLoginCardsModal
          }
        },
        {
          label: 'Print Student Certificates',
          MenuItemProps: {
            onClick: openPrintCertificatesModal
          }
        },
        {
          label: 'Edit Student Details',
          MenuItemProps: { onClick: handleEditStudentDetailsClick },
          isPrivileged: (): boolean =>
            userPrivilegesCache.isPrivileged(privilegesGroups.mutateStudent, {
              classroomId,
              organizationId: classroom?.organizationId
            })
        },
        {
          label: 'Transfer Student',
          MenuItemProps: { onClick: handleTransferStudentClick },
          isPrivileged: (): boolean =>
            userPrivilegesCache.isPrivileged(privilegesGroups.mutateStudent, {
              classroomId,
              organizationId: classroom?.organizationId
            })
        },
        ...archivedOptions
      ];

  useRouteAction(({ action, params }) => {
    if (!action) return;
    switch (action) {
      case ClassroomModalRouteAction.EDIT_STUDENT_PRODUCTS:
        return openEditStudentProductsModal(params.studentId);
      case ClassroomModalRouteAction.EDIT_STUDENT:
        return openEditStudentDetailsModal(params.studentId, params.classroomId);
      case ClassroomModalRouteAction.EDIT_STUDENT_SETTINGS:
        return openEditStudentSettingsModal(params.studentId);
      case ClassroomModalRouteAction.TRANSFER_STUDENT:
        return openTransferStudentDetailsModal(params.studentId);

      default:
        return null;
    }
  });

  return {
    actionOptions,
    archivedOptions,
    loading: removeStudentMutationLoading
  };
};

export default useStudentActionOptionsMenuProps;
